import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    Tab, Table, TableBody,
    TableCell, TableContainer,
    TableHead,
    TableRow,
    Tabs, TextField,
} from '@mui/material';
import AutoSearch, { Option } from 'components/common/AutoSearch';
import { a11yProps, TabPanel } from 'components/mui/Tabs';
import { ModalActionProps } from 'models/AutoModals';
import { PriceTableModel } from 'models/PriceTablesModel';
import priceTablesAtom from 'recoil/atoms/priceTables';
import Api from 'utils/api';


type PriceTablesDialogProps = {
    portId: number,
} & ModalActionProps

const tabs = [
    'ports',
    'yards',
    'companies',
];

const PriceTablesDialog = ({portId, onFinish}: PriceTablesDialogProps) => {
    const {t} = useTranslation();
    const tables = useRecoilValue(priceTablesAtom);
    const [tab, setTab] = useState(0);
    const [port, setPort] = useState(portId);

    if (!tables.data)
        return null;

    const submit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        onFinish(new FormData(e.currentTarget));
    };

    const id = 'tables';

    const groupedYardsTable: any = tables.data.yard; // TODO

    return (
        <Box component="form" onSubmit={submit}>
            <DialogContent sx={{paddingBottom: 0}}>
                <Box
                    sx={{
                        flexGrow: 1,
                        bgcolor: 'background.paper',
                        display: 'flex',
                    }}
                >
                    <Tabs
                        orientation="vertical"
                        value={tab}
                        onChange={(_, val) => setTab(val)}
                        aria-label="price tables"
                        sx={{borderRight: 1, borderColor: 'divider'}}
                    >
                        {tabs.map(tab => (
                            <Tab key={tab} label={t(tab)} {...a11yProps(id, tab)} />
                        ))}
                    </Tabs>

                    <TabPanel
                        open={tab === 0}
                        id={id}
                        index={0}
                        keepMounted
                    >
                        <PriceList
                            rows={tables.meta.ports}
                            list={tables.data.port[port].freight as any}
                            prefix={`[port][${port}][freight]`}
                        />

                        <PriceTable
                            cols={tables.meta.vehicles.cols}
                            rows={tables.meta.vehicles.rows}
                            table={tables.data.port[port].vehicle as any}
                            prefix={`[port][${port}][vehicle]`}
                        />
                    </TabPanel>

                    <TabPanel
                        open={tab === 1}
                        id={id}
                        index={1}
                        keepMounted
                    >
                        <PriceTable
                            cols={tables.meta.yards.cols}
                            rows={tables.meta.yards.rows}
                            table={groupedYardsTable}
                            prefix={`[yard]`}
                        />
                    </TabPanel>

                    <TabPanel
                        open={tab === 2}
                        id={id}
                        index={2}
                        keepMounted
                    >
                        <CompanyPriceLists
                            shipperData={tables.data.shipper}
                            shipperRows={tables.meta.shippers}
                            partyData={tables.data.party}
                            partyRows={tables.meta.parties}
                        />
                    </TabPanel>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button type="submit">{t('save')}</Button>
            </DialogActions>
        </Box>
    );
};

type PriceTableProps = {
    cols: string[],
    rows: string[],
    table: PriceTableModel,
    prefix: string,
}

const PriceTable = ({cols, rows, table, prefix}: PriceTableProps) => {
    const {t} = useTranslation();

    return (
        <TableContainer>
            <Table
                sx={{
                    '.MuiTableCell-head': {
                        padding: '6px',
                    },
                    '.MuiTableCell-body': {
                        padding: 0,
                        borderBottom: 0,

                        '&:first-of-type': {
                            padding: '6px',
                            width: 150,
                            lineHeight: '12px',
                        },

                        '.MuiOutlinedInput-root': {
                            borderRadius: 0,
                        },
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell></TableCell>
                        {cols.map(col => (
                            <TableCell key={col}>{t(col)}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Object.entries(table).map(([row, cols], i) => (
                        <TableRow key={row}>
                            <TableCell>
                                {rows[i]}
                            </TableCell>
                            {Object.entries(cols).map(([col, value]) => (
                                <TableCell key={col}>
                                    <TextField
                                        margin="none"
                                        variant="outlined"
                                        name={`prices${prefix}[${row.replace('#', '')}][${col}]`}
                                        sx={{width: 115}}
                                        inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                                        defaultValue={value || ''}
                                    />
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
};

type PriceListProps = {
    rows: {[id: number]: string},
    list: {[id: number]: number},
    prefix: string,
}

const PriceList = ({rows, list, prefix}: PriceListProps) => {
    return (
        <TableContainer>
            <Table
                sx={{
                    '.MuiTableCell-head': {
                        padding: '6px',
                    },
                    '.MuiTableCell-body': {
                        padding: 0,
                        borderBottom: 0,

                        '&:first-of-type': {
                            padding: '6px',
                            width: 150,
                        },

                        '.MuiOutlinedInput-root': {
                            borderRadius: 0,
                        },
                    },
                }}
            >
                <TableBody>
                    {Object.entries(list).map(([id, value]) => (
                        <TableRow key={id}>
                            <TableCell>
                                {rows[Number(id)]}
                            </TableCell>

                            <TableCell>
                                <TextField
                                    margin="none"
                                    variant="outlined"
                                    name={`prices${prefix}[${id}]`}
                                    sx={{width: 100}}
                                    inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                                    defaultValue={value || ''}
                                />
                            </TableCell>

                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
};


type CompanyPriceTableProps = {
    shipperData: any,
    shipperRows: any,
    partyData: any,
    partyRows: any,
}

type Holder = {
    id: number,
    name: string,
    value: number,
};


const CompanyPriceLists = ({shipperData, shipperRows, partyData, partyRows}: CompanyPriceTableProps) => {
    const [shippers, setShippers] = useState<[number, string][]>([]);
    const [parties, setParties] = useState<[number, string][]>([]);

    const fob: Holder[] = [];
    const caf: Holder[] = [];

    Object.entries(shipperData).forEach(([id, cols]) => {
        // @ts-ignore
        caf.push({id: Number(id), name: shipperRows[id], value: cols.discount})
    });
    Object.entries(partyData).forEach(([id, cols]) => {
        // @ts-ignore
        fob.push({id: Number(id), name: partyRows[id], value: cols.discount})
    });

    const types = [
        ['shipper', caf, shippers],
        ['party', fob, parties],
    ] as [string, Holder[], any[]][];

    const addShipper = (val: Option | null) => {
        if (!val)
            return;

        for (const shipper of shippers) {
            if (shipper[0] === val.id)
                return;
        }

        setShippers(old => ([...old, [val.id, val.label]]));
    };

    const addParty = (val: Option | null) => {
        if (!val)
            return;

        for (const party of parties) {
            if (party[0] === val.id)
                return;
        }

        setParties(old => ([...old, [val.id, val.label]]));
    };

    return (
        <TableContainer>
            <Table
                sx={{
                    '.MuiTableCell-head': {
                        padding: '6px',
                    },
                    '.MuiTableCell-body': {
                        padding: 0,

                        '&:first-of-type': {
                            padding: '6px',
                        }
                    },
                }}
            >
                {types.map(([type, items, newItems]) => (
                    <Fragment key={type}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{type} discount</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {items.map(({id, name, value}) => (
                                <TableRow key={id}>
                                    <TableCell>
                                        {name}
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            margin="none"
                                            variant="outlined"
                                            name={`prices[${type}][${id}][discount]`}
                                            fullWidth
                                            inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                                            defaultValue={value || ''}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}

                            {newItems.map(([id, name]) => (
                                <TableRow key={id}>
                                    <TableCell>
                                        {name} *
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            margin="none"
                                            variant="outlined"
                                            name={`prices[${type}][${id}][discount]`}
                                            fullWidth
                                            inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}

                            <TableRow>
                                <TableCell colSpan={2}>
                                    {type === 'party' ? (
                                        <AutoSearch
                                            search={s => Api.parties.search(s, 'dealer')}
                                            onChange={addParty}
                                            margin="none"
                                        />
                                    ) : (
                                        <AutoSearch
                                            search={Api.company.search}
                                            onChange={addShipper}
                                            margin="none"
                                        />
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Fragment>
                ))}
            </Table>
        </TableContainer>
    )
};

export default PriceTablesDialog;
